import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { History, User, Shield, Lock, Database, KeyRound, Trash2, FileEdit, LogIn, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_ICONS = {
  CREATE: FileEdit,
  UPDATE: FileEdit,
  DELETE: Trash2,
  LOGIN: LogIn,
  LOGOUT: LogOut,
  BLOCK: Lock,
  UNBLOCK: Shield,
  RESET_PASSWORD: KeyRound,
  CHANGE_PASSWORD: KeyRound,
  BACKUP: Database,
  UPDATE_ROLE_PERMISSIONS: Shield,
  RESET_ROLE_PERMISSIONS: Shield,
};

const ACTION_COLORS = {
  CREATE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-rose-50 text-rose-700 border-rose-200',
  LOGIN: 'bg-sky-50 text-sky-700 border-sky-200',
  LOGOUT: 'bg-slate-50 text-slate-600 border-slate-200',
  BLOCK: 'bg-rose-50 text-rose-700 border-rose-200',
  UNBLOCK: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  RESET_PASSWORD: 'bg-amber-50 text-amber-700 border-amber-200',
  CHANGE_PASSWORD: 'bg-amber-50 text-amber-700 border-amber-200',
  BACKUP: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  UPDATE_ROLE_PERMISSIONS: 'bg-violet-50 text-violet-700 border-violet-200',
  RESET_ROLE_PERMISSIONS: 'bg-slate-50 text-slate-600 border-slate-200',
};

export default function AuditLogs() {
  const { user } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('ALL');
  const [entity, setEntity] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [action, entity]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (action !== 'ALL') params.action = action;
      if (entity !== 'ALL') params.entity_type = entity;
      const r = await axios.get(`${API}/audit-logs`, { params, withCredentials: true });
      setLogs(r.data || []);
    } catch (e) {
      toast.error('Failed to load audit log');
    }
    setLoading(false);
  };

  const filtered = logs.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.user_email || '').toLowerCase().includes(s)
      || (l.summary || '').toLowerCase().includes(s)
      || (l.entity_type || '').toLowerCase().includes(s);
  });

  const actionTypes = Array.from(new Set(logs.map(l => l.action))).sort();
  const entityTypes = Array.from(new Set(logs.map(l => l.entity_type).filter(Boolean))).sort();

  return (
    <Layout>
      <div data-testid="audit-logs-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <History className="w-6 h-6 text-amber-600" />Audit Log
          </h1>
          <p className="text-sm text-muted-foreground">Immutable trail of every high-impact action in your tenant — user changes, password resets, backups, role-permission updates.</p>
        </div>

        <Card className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Action</label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All actions</SelectItem>
                  {actionTypes.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Entity</label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All entities</SelectItem>
                  {entityTypes.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Search</label>
              <Input data-testid="audit-search" placeholder="Email, summary, entity…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No audit entries match the filters.</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {filtered.map((l, idx) => {
                const Icon = ACTION_ICONS[l.action] || FileEdit;
                const color = ACTION_COLORS[l.action] || 'bg-slate-50 text-slate-700 border-slate-200';
                return (
                  <div key={l.id || idx} data-testid={`audit-row-${idx}`} className="flex items-start gap-4 p-4 hover:bg-slate-50/60">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${color} flex-shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-semibold text-foreground">{l.action.replace(/_/g, ' ')}</span>
                        {l.entity_type && <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{l.entity_type}</span>}
                        <span className="text-xs text-muted-foreground">· {new Date(l.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-slate-700 mt-0.5">{l.summary || '—'}</div>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                        <User className="w-3 h-3" />
                        <span>{l.user_email || 'system'}</span>
                        {l.user_role && <span className="text-slate-400">({l.user_role})</span>}
                        {l.ip && <span className="text-slate-400">· {l.ip}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        {user?.role === 'SUPERADMIN' && (
          <p className="text-xs text-muted-foreground">SUPERADMIN view — showing entries across all tenants.</p>
        )}
      </div>
    </Layout>
  );
}
